import React from 'react'
import { Image } from 'antd'

import type { HistoryPrediction } from '../../types/history'
import {
    getAnnotationClassColor,
    getStableModelTypeColor,
    normalizeAnnotationTypeKey,
} from '../../utils/annotationColors'
import { getImageUrl } from '../../utils/urlHelper'
import { ImagePreviewHoverMask } from './ImagePreviewHoverMask'
import {
    buildReadonlyPreviewRender,
    READONLY_PREVIEW_IMAGE_STYLE,
    type ReadonlyPreviewImageRender,
} from './MediaPreview/readonlyPreviewRender'

type ReadonlyDetectionOverlayPrediction =
    | HistoryPrediction
    | Record<string, unknown>

interface BoxBounds {
    x1: number
    y1: number
    x2: number
    y2: number
    strokeColor?: string
}

interface ImageNaturalSize {
    width: number
    height: number
}

interface ReadonlyDetectionOverlayImageProps {
    imageSrc?: string | null
    fallbackImageSrc?: string | null
    predictions?: ReadonlyDetectionOverlayPrediction[] | null
    alt: string
    width?: React.CSSProperties['width']
    height?: React.CSSProperties['height']
    minHeight?: React.CSSProperties['minHeight']
    maxHeight?: React.CSSProperties['maxHeight']
    className?: string
    preview?: boolean
    previewMaskClosable?: boolean
    previewTitle?: React.ReactNode
    containPreview?: boolean
    emptyText?: React.ReactNode
    strokeColor?: string
    colorizePredictions?: boolean
    strokeWidth?: number
    borderRadius?: React.CSSProperties['borderRadius']
}

const DEFAULT_BOX_STROKE_COLOR = '#000000'
const DEFAULT_BOX_STROKE_WIDTH = 1.8

const toFiniteNumber = (value: unknown): number | null => {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : null
}

const normalizeBoxBounds = (
    x1: unknown,
    y1: unknown,
    x2: unknown,
    y2: unknown
): BoxBounds | null => {
    const left = toFiniteNumber(x1)
    const top = toFiniteNumber(y1)
    const right = toFiniteNumber(x2)
    const bottom = toFiniteNumber(y2)

    if (left === null || top === null || right === null || bottom === null) {
        return null
    }

    const normalized: BoxBounds = {
        x1: Math.min(left, right),
        y1: Math.min(top, bottom),
        x2: Math.max(left, right),
        y2: Math.max(top, bottom),
    }

    if (normalized.x2 <= normalized.x1 || normalized.y2 <= normalized.y1) {
        return null
    }

    return normalized
}

const getReadonlyDetectionOverlayBoxes = (
    predictions?: ReadonlyDetectionOverlayPrediction[] | null,
    colorizePredictions = false
): BoxBounds[] => {
    if (!Array.isArray(predictions)) {
        return []
    }

    return predictions.flatMap((prediction) => {
        const bbox = (prediction as { bbox?: unknown }).bbox
        let box: BoxBounds | null = null
        if (bbox && typeof bbox === 'object' && !Array.isArray(bbox)) {
            const bounds = bbox as Record<string, unknown>
            box = normalizeBoxBounds(bounds.x1, bounds.y1, bounds.x2, bounds.y2)
        }

        const boxArray = (prediction as { box?: unknown }).box
        if (!box && Array.isArray(boxArray) && boxArray.length >= 4) {
            box = normalizeBoxBounds(
                boxArray[0],
                boxArray[1],
                boxArray[2],
                boxArray[3],
            )
        }

        if (!box) {
            return []
        }
        if (!colorizePredictions) {
            return [box]
        }

        const typedPrediction = prediction as Partial<HistoryPrediction>
        const modelKey = normalizeAnnotationTypeKey(
            typedPrediction.model_key || 'mosquito',
        )
        const rawClass = normalizeAnnotationTypeKey(
            typedPrediction.raw_class_norm || typedPrediction.raw_class,
        )
        const identityKey =
            modelKey && rawClass ? `${modelKey}:${rawClass}` : ''
        const classKey =
            typedPrediction.display_category_key ||
            typedPrediction.eng_class ||
            typedPrediction.class
        return [
            {
                ...box,
                strokeColor:
                    typedPrediction.color ||
                    (identityKey
                        ? getStableModelTypeColor(identityKey)
                        : getAnnotationClassColor(classKey)),
            },
        ]
    })
}

export const ReadonlyDetectionOverlayImage: React.FC<
    ReadonlyDetectionOverlayImageProps
> = ({
    imageSrc,
    fallbackImageSrc,
    predictions,
    alt,
    width = '100%',
    height = 320,
    minHeight,
    maxHeight,
    className,
    preview = false,
    previewMaskClosable = true,
    previewTitle,
    emptyText,
    strokeColor = DEFAULT_BOX_STROKE_COLOR,
    colorizePredictions = false,
    strokeWidth = DEFAULT_BOX_STROKE_WIDTH,
    borderRadius = 6,
}) => {
    const resolvedImageSrc = imageSrc || fallbackImageSrc || null
    const [naturalSize, setNaturalSize] =
        React.useState<ImageNaturalSize | null>(null)
    const [imageLoadFailed, setImageLoadFailed] = React.useState(false)
    const [previewVisible, setPreviewVisible] = React.useState(false)
    const [previewHovered, setPreviewHovered] = React.useState(false)
    const boxes = React.useMemo(
        () =>
            getReadonlyDetectionOverlayBoxes(predictions, colorizePredictions),
        [colorizePredictions, predictions],
    )
    const imageUrl = resolvedImageSrc ? getImageUrl(resolvedImageSrc) : undefined
    const previewLabel =
        typeof previewTitle === 'string' && previewTitle.trim()
            ? previewTitle
            : alt

    React.useEffect(() => {
        setNaturalSize(null)
        setImageLoadFailed(false)
    }, [resolvedImageSrc])

    React.useEffect(() => {
        if (!imageUrl) {
            return
        }

        let cancelled = false
        const preloadImage = new window.Image()
        preloadImage.onload = () => {
            if (cancelled) {
                return
            }
            setNaturalSize({
                width: preloadImage.naturalWidth,
                height: preloadImage.naturalHeight,
            })
        }
        preloadImage.onerror = () => {
            if (!cancelled) {
                setImageLoadFailed(true)
            }
        }
        preloadImage.src = imageUrl

        return () => {
            cancelled = true
        }
    }, [imageUrl])

    if (!resolvedImageSrc || imageLoadFailed) {
        return <span style={{ color: '#999' }}>{emptyText}</span>
    }

    const renderOverlay = (
        size: ImageNaturalSize,
        currentStrokeWidth = strokeWidth
    ) => (
        <svg
            data-testid="readonly-detection-overlay-svg"
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1,
            }}
        >
            {boxes.map((box, index) => (
                <rect
                    key={`${box.x1}-${box.y1}-${box.x2}-${box.y2}-${index}`}
                    data-testid="readonly-detection-overlay-box"
                    x={box.x1}
                    y={box.y1}
                    width={box.x2 - box.x1}
                    height={box.y2 - box.y1}
                    fill="transparent"
                    stroke={box.strokeColor || strokeColor}
                    strokeWidth={currentStrokeWidth}
                    vectorEffect="non-scaling-stroke"
                />
            ))}
        </svg>
    )

    const renderPreviewImage: ReadonlyPreviewImageRender =
        buildReadonlyPreviewRender((_info, motionStyle) =>
            naturalSize
                ? React.cloneElement(
                      renderOverlay(
                          naturalSize,
                          Math.max(strokeWidth, DEFAULT_BOX_STROKE_WIDTH),
                      ),
                      {
                          height: naturalSize.height,
                          width: naturalSize.width,
                          style: {
                              ...READONLY_PREVIEW_IMAGE_STYLE,
                              display: 'block',
                              pointerEvents: 'none',
                              ...motionStyle,
                          },
                      },
                  )
                : null,
        )

    const frameStyle: React.CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width,
        height,
        minHeight,
        maxHeight,
        overflow: 'hidden',
        borderRadius,
        background: '#f6f8fb',
        lineHeight: 0,
    }

    const frameContent = (
        <>
            <img
                src={imageUrl}
                alt={alt}
                draggable={false}
                loading="lazy"
                decoding="async"
                onLoad={(event) => {
                    setNaturalSize({
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                    })
                }}
                onError={() => setImageLoadFailed(true)}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    userSelect: 'none',
                }}
            />
            {naturalSize && renderOverlay(naturalSize)}
        </>
    )

    return (
        <>
            {preview ? (
                <button
                    type="button"
                    className={className}
                    data-box-count={boxes.length}
                    data-testid="readonly-detection-overlay-image"
                    aria-label={previewLabel}
                    title={previewLabel}
                    onClick={() => setPreviewVisible(true)}
                    onMouseEnter={() => setPreviewHovered(true)}
                    onMouseLeave={() => setPreviewHovered(false)}
                    onFocus={() => setPreviewHovered(true)}
                    onBlur={() => setPreviewHovered(false)}
                    style={{
                        ...frameStyle,
                        padding: 0,
                        border: 0,
                        cursor: 'pointer',
                    }}
                >
                    {frameContent}
                    <ImagePreviewHoverMask
                        label={previewLabel}
                        visible={previewHovered}
                    />
                </button>
            ) : (
                <div
                    className={className}
                    data-box-count={boxes.length}
                    data-testid="readonly-detection-overlay-image"
                    style={frameStyle}
                >
                    {frameContent}
                </div>
            )}
            {preview && (
                <Image
                    src={imageUrl}
                    alt=""
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewVisible,
                        src: imageUrl,
                        maskClosable: previewMaskClosable,
                        onVisibleChange: (visible) =>
                            setPreviewVisible(visible),
                        imageRender: renderPreviewImage,
                    }}
                />
            )}
        </>
    )
}
